var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
import cls from 'classnames';
import { debounce } from 'debounce';
import { forceStyleRecalculation } from '../utils/forceStyleRecalculation';
import { stickElement } from '../utils/stickElement';
import { useTranslations } from '../hooks/useTranslations';
import { SearchInput } from './SearchInput';
import { DeviceType, getDeviceType } from '../utils/getDeviceType';
const calculateAdditionalStylesFromEnv = () => {
    var _a;
    if ((_a = window.WH) === null || _a === void 0 ? void 0 : _a.NGS) {
        return {};
    }
    const sportsbookContentBelowHeader = document.querySelector('.off-canvas__inner-wrapper');
    if (!sportsbookContentBelowHeader) {
        return {};
    }
    return { marginTop: sportsbookContentBelowHeader.getBoundingClientRect().y };
};
export function SearchPanel(_a) {
    var { id, value, open, center, loading, placeholder, className, onChange, onClose, onFocus, children, dangerouslySetInnerHTML, sportsInEntries, setUsedSport, currentSportFilter, selectedFilterReference } = _a, props = __rest(_a, ["id", "value", "open", "center", "loading", "placeholder", "className", "onChange", "onClose", "onFocus", "children", "dangerouslySetInnerHTML", "sportsInEntries", "setUsedSport", "currentSportFilter", "selectedFilterReference"]);
    const deviceType = getDeviceType();
    const { t } = useTranslations();
    const [activated, setActivated] = useState(open);
    const [yScroll, setYScroll] = useState(0);
    const [lastScrollHandled, setLastScrollHandled] = useState(0);
    const [scrollForward, setScrollForward] = useState(false);
    const [betslipHeight, setBetslipHeight] = useState(document.getElementsByClassName('bs-betslip__view-layer').length > 0
        ? document.getElementsByClassName('bs-betslip__view-layer')[0].clientHeight
        : 0);
    const contentRef = useRef(null);
    const ref = useRef(null);
    const rootWidth = document.getElementsByClassName('WhSearch-sbWrapper').length > 0
        ? document.getElementsByClassName('WhSearch-sbWrapper')[0].clientWidth
        : Infinity;
    const useDesktopStyles = rootWidth > 1024;
    const [onScroll] = useState(() => debounce((element, yScroll, lastScrollHandled) => {
        const isElementEnd = element.scrollHeight - element.scrollTop - element.offsetHeight < 10;
        const nextScrollTop = element.scrollTop;
        if (!isElementEnd) {
            setYScroll(nextScrollTop);
        }
        if (Math.abs(lastScrollHandled - nextScrollTop) > 10 && !isElementEnd) {
            setScrollForward(nextScrollTop > yScroll);
            setLastScrollHandled(nextScrollTop);
        }
    }, 10));
    const toolbarReference = document.getElementById('toolbar');
    const additionalPaddingForMobileWeb = (deviceType === DeviceType.MobileWeb || deviceType === DeviceType.MobileNativeAndroid) &&
        toolbarReference
        ? toolbarReference.clientHeight
        : 0;
    useEffect(() => {
        if (open === undefined) {
            return;
        }
        if (ref.current) {
            const element = ref.current.querySelector('.WhSearch-SearchInput');
            const inputElement = element.querySelector('input');
            if (open) {
                const unstick = stickElement(element);
                forceStyleRecalculation(element);
                setActivated(true);
                inputElement.focus();
                unstick();
            }
            else {
                setActivated(false);
                inputElement.blur();
            }
        }
        else {
            setActivated(open);
        }
    }, [open]);
    const finalClassName = cls('WhSearch-SearchPanel', className, {
        'WhSearch-SearchPanel--center': center,
        'WhSearch-SearchPanel--activated': activated,
        'WhSearch-SearchPanel--scrolled': scrollForward && yScroll > 50 && deviceType !== DeviceType.Desktop,
        'WhSearch-SearchPanel--loading': loading,
    });
    useEffect(() => {
        var _a;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (_a = window === null || window === void 0 ? void 0 : window.WH) === null || _a === void 0 ? void 0 : _a.messageBus.subscribe('wh.sports.betslip', (topic, data) => {
            if (data) {
                setTimeout(() => {
                    setBetslipHeight(document.getElementsByClassName('bs-betslip__view-layer').length > 0
                        ? document.getElementsByClassName('bs-betslip__view-layer')[0].clientHeight
                        : 0);
                }, 500);
            }
            else {
                setBetslipHeight(0);
            }
        });
    }, []);
    const additionalStyles = calculateAdditionalStylesFromEnv();
    return (React.createElement("div", Object.assign({ className: finalClassName, ref: ref }, props),
        React.createElement("div", { className: "WhSearch-SearchPanel__closedInputSkeleton" }),
        React.createElement(SearchInput, { id: id, value: value, loading: loading, placeholder: placeholder, onClose: onClose, onChange: onChange, onFocus: onFocus, activated: activated, style: additionalStyles, useDesktopStyles: useDesktopStyles }),
        React.createElement("div", { className: cls({
                'WhSearch-SearchPanel__background--desktop': useDesktopStyles,
            }) },
            React.createElement("div", { className: cls('WhSearch-SearchPanel__viewport', {
                    'WhSearch-SearchPanel__viewport--desktop': useDesktopStyles,
                }), style: additionalStyles },
                React.createElement("div", { className: cls('WhSearch-SearchPanel__header', {
                        'WhSearch-SearchPanel__header_with_Filters': !!sportsInEntries,
                    }) },
                    React.createElement("header", { className: "WhSearch-SearchPanel__headerContent" },
                        React.createElement("span", { className: "WhSearch-SearchPanel__headerTitle" }, t('searchTitle'))),
                    React.createElement("div", { "aria-hidden": "true", className: "WhSearch-SearchPanel__openedInputSkeleton" }),
                    sportsInEntries && (React.createElement("div", { className: "WhSearch-SearchPanel__FiltersWrapper" },
                        React.createElement("div", { className: "WhSearch-SearchPanel__FilterList" }, sportsInEntries.map((sport) => {
                            return sport.id === currentSportFilter ? (React.createElement("div", { ref: selectedFilterReference, className: cls('WhSearch-SearchPanel__FilterListElement', 'WhSearch-SearchPanel__FilterList_SelectedElement'), onClick: () => setUsedSport && setUsedSport(sport.id) }, sport.name)) : (React.createElement("div", { className: cls('WhSearch-SearchPanel__FilterListElement'), onClick: () => setUsedSport && setUsedSport(sport.id) }, sport.name));
                        }))))),
                React.createElement("div", { className: "WhSearch-SearchPanel__content" },
                    React.createElement("div", { ref: contentRef, className: cls('WhSearch-SearchPanel__overflowContainer', {
                            'WhSearch-SearchPanel__overflowContainer--desktop': useDesktopStyles,
                        }), onScroll: () => onScroll(contentRef.current, yScroll, lastScrollHandled) },
                        React.createElement("div", { className: cls('WhSearch-SearchPanel__contentContainer', {
                                'WhSearch-SearchPanel__contentContainer--desktop': useDesktopStyles,
                            }, {
                                'WhSearch-SearchPanel__contentContainer_with_filters': !!sportsInEntries,
                            }, {
                                'WhSearch-SearchPanel__contentContainer_with_filters--desktop': useDesktopStyles && !!sportsInEntries,
                            }), dangerouslySetInnerHTML: dangerouslySetInnerHTML, style: { paddingBottom: betslipHeight + additionalPaddingForMobileWeb + 'px' } }, children)))))));
}
//# sourceMappingURL=SearchPanel.js.map